/**
 * What you can do to a whole comanda at once.
 *
 * "Tutta la comanda pronta" is offered only when the kitchen actually has
 * something called and still cooking — a wave nobody has sent in is not the
 * cook's to finish, and offering to finish it produces a ticket that says done
 * while the waiter has not asked for it yet.
 */
import { CheckCircle2, Flame } from "lucide-react";
import { useTranslation } from "../i18n";

interface OrderCardActionsProps {
  /** Chef view: the single big "everything is ready" button. */
  compact?: boolean;
  hasCookingFired: boolean;
  showFireCourse: boolean;
  showComplete: boolean;
  nextWave: number;
  loadingAction: "fire" | "complete" | null;
  onMarkAllReady: () => void;
  onFireNextWave: () => void;
  onComplete: () => void;
}

function Spinner() {
  return (
    <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
  );
}

export default function OrderCardActions({
  compact,
  hasCookingFired,
  showFireCourse,
  showComplete,
  nextWave,
  loadingAction,
  onMarkAllReady,
  onFireNextWave,
  onComplete,
}: OrderCardActionsProps) {
  const { t } = useTranslation();
  if (compact) {
    if (!hasCookingFired) return null;
    return (
      <button
        onClick={onMarkAllReady}
        disabled={loadingAction !== null}
        className="mt-3 w-full flex items-center justify-center gap-3 bg-kds-ready text-white rounded-lg px-4 py-2 text-kds-action min-h-touch transition-opacity disabled:opacity-50 hover:brightness-110"
        title={t("kds.actions.allReadyTitle", "Segna tutti i piatti come pronti — il cameriere verrà notificato")}
      >
        {loadingAction === "complete" ? <Spinner /> : <CheckCircle2 size={28} />}
        {t("kds.actions.allReady", "Tutta la comanda pronta")}
      </button>
    );
  }

  if (!showFireCourse && !showComplete) return null;

  return (
    <div className="mt-3 flex gap-2">
      {showFireCourse && (
        <button
          onClick={onFireNextWave}
          disabled={loadingAction !== null}
          className={[
            "flex-1 flex items-center justify-center gap-3",
            "bg-kds-preparing text-white rounded-lg px-4 py-2",
            "text-kds-action min-h-touch",
            "transition-opacity disabled:opacity-50",
            loadingAction === "fire" ? "opacity-60 cursor-wait" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {loadingAction === "fire" ? <Spinner /> : <Flame size={26} />}
          {t("kds.actions.fireWave", "Invia Ondata {{wave}}", { wave: nextWave })}
        </button>
      )}

      {showComplete && (
        <button
          onClick={onComplete}
          disabled={loadingAction !== null}
          className={[
            "flex-1 flex items-center justify-center gap-3",
            "bg-kds-ready text-white rounded-lg px-4 py-2",
            "text-kds-action min-h-touch",
            "transition-opacity disabled:opacity-50",
            loadingAction === "complete" ? "opacity-60 cursor-wait" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {loadingAction === "complete" ? <Spinner /> : <CheckCircle2 size={26} />}
          {t("kds.actions.complete", "Completa")}
        </button>
      )}
    </div>
  );
}
